import React, { useState } from "react";
import { DashboardLayout } from "./Dashboard";
import RHFWrapper from "@/components/RHFWrapper";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import RhfMultiInput from "@/components/rhfFields/RhfMultiInput";
import { Button } from "@/components/ui/button";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RhfAsyncSelect from "@/components/rhfFields/RhfAsyncSelect";
import { Trash2, Plus } from "lucide-react";
import { questionSchema } from "@/utils/ValidataionSchemas";
import { useGetData } from "@/hooks/useGetData";
import AxiosInstance from "@/utils/AxiosInstance";
import { useLocation, useNavigate, useParams } from "react-router";
import { getPromise, getPromiseToast } from "@/utils/helpers";

const Questionconfig = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const { state } = useLocation();
  const [btnloading, setBtnLoading] = useState(false);
  // const nav= useNavigate();
  async function handlePostConfig(formdata: any) {
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setBtnLoading(true);
      if (isEdit) {
        const { data } = await AxiosInstance().put("/icd-questionnaire/" + id, {
          ...formdata?.[0],
        });
        resolveFn("Template updated");
      } else {
        const { data } = await AxiosInstance().post("/icd-questionnaire", {
          ...formdata?.[0],
        });
        resolveFn("Template saved");
      }
      nav("/config");
    } catch (error: any) {
      rejectFn(error);
      console.log(error.message);
    } finally {
      setBtnLoading(false);
    }
  }

  const defaultvalue = isEdit
    ? {
        sections: [
          {
            icdCodes: state?.data?.icdCodes,
            questions: state?.data?.questions,
            name: state?.data?.name,
          },
        ],
      }
    : {
        sections: [
          {
            icdCodes: [],
            questions: [{ question: "", type: "", options: [] }],
            name: "",
          },
        ],
      };
  return (
    <DashboardLayout
      activePage="/config"
      // rightcomponent={<Button onClick={() => nav(-1)}>Add Template</Button>}
    >
      <RHFWrapper
        initialValues={defaultvalue}
        isValidate={true}
        onSubmit={(data) => {
          // Transform data to match the required schema
          const transformedData = data.sections.map((section: any) => ({
            icdCodes: section.icdCodes || [],
            name: section?.name,
            questions: section.questions.map((q: any) => ({
              question: q.question,
              options: q.options || [],
              type: q.type,
            })),
          }));
          console.log("Final Answer Schema:", transformedData, data);
          handlePostConfig(transformedData);
        }}
        validateSchema={questionSchema}
        className="  "
      >
        <QuestionForm isEdit={isEdit} />
        <div className="   bottom-0  bg-white border-t p-4 shadow-lg z-[1000]">
          {/* <div className="max-w-7xl mx-auto"> */}
          <Button disabled={btnloading} type="submit" className="w-full">
            {isEdit ? "Update Template" : "Submit"}
          </Button>
          {/* </div> */}
        </div>
      </RHFWrapper>
    </DashboardLayout>
  );
};

export default Questionconfig;

const QuestionForm = ({ isEdit }: any) => {
  const { control } = useFormContext();
  const {
    fields: sections,
    append: appendSection,
    remove: removeSection,
  } = useFieldArray({
    control,
    name: "sections",
  });

  return (
    <div className="pb-0">
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => (
          <SectionCard
            key={section.id}
            sectionIndex={sectionIndex}
            onRemove={() => removeSection(sectionIndex)}
            showRemove={sections.length > 1}
            isEdit={isEdit}
          />
        ))}
      </div>
    </div>
  );
};

const SectionCard = ({ sectionIndex, onRemove, showRemove, isEdit }: any) => {
  const { control } = useFormContext();
  const {
    fields: questions,
    append: appendQuestion,
    remove: removeQuestion,
  } = useFieldArray({
    control,
    name: `sections.${sectionIndex}.questions`,
  });

  const isFirstSection = sectionIndex === 0;

  return (
    <div className="border rounded-lg p-6 space-y-4 bg-white shadow-sm">
      <div className="flex items-center justify-between">
        {/* <h2 className="text-xl font-semibold">Section {sectionIndex + 1}</h2> */}
        {showRemove && !isFirstSection && (
          <Button
            type="button"
            onClick={onRemove}
            variant="destructive"
            size="sm"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      <RhfInput
        name={`sections.${sectionIndex}.name`}
        label="Template name"
        required
      />

      {/* {isEdit ? (
        <RhfSelect
          options={[]}
          multiple
          label="Select Multiple ICD Codes"
          name={`sections.${sectionIndex}.icdCodes`}
          placeholder="Search for ICD codes..."
          required
          // disabled={isEdit}
        />
      ) : ( */}
      <RhfAsyncSelect
        label="Select Multiple ICD Codes"
        name={`sections.${sectionIndex}.icdCodes`}
        isCache
        asyncConfig={{
          apiUrl: "https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search",
          searchParam: "terms",
          minSearchLength: 1,
          responseMapper: (response) =>
            response?.[3]?.map((entry: any[]) => ({
              label: `${entry?.[0]} - ${entry?.[1]}`,
              value: `${entry?.[0]} - ${entry?.[1]}`,
            })) ?? [],
        }}
        placeholder="Search for ICD codes..."
        required
        // disabled={isEdit}
        multiple
      />
      {/* )} */}

      <div className="space-y-4 mt-6">
        <h3 className="text-lg font-medium">Questions for Above ICD Codes</h3>

        {questions.map((question, questionIndex) => {
          const isFirstQuestion = sectionIndex === 0 && questionIndex === 0;
          return (
            <QuestionCard
              key={question.id}
              sectionIndex={sectionIndex}
              questionIndex={questionIndex}
              onRemove={() => removeQuestion(questionIndex)}
              showRemove={!isFirstQuestion}
            />
          );
        })}

        <Button
          type="button"
          onClick={() =>
            appendQuestion({ question: "", type: "", options: [] })
          }
          variant="outline"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
           Question
        </Button>
      </div>
    </div>
  );
};

const QuestionCard = ({
  sectionIndex,
  questionIndex,
  onRemove,
  showRemove,
}: any) => {
  const questionType = useWatch({
    name: `sections.${sectionIndex}.questions.${questionIndex}.type`,
  });

  const showOptions =
    questionType === "multiple_choice" || questionType === "single_choice";

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Question {questionIndex + 1}</h4>
        {showRemove && (
          <Button type="button" onClick={onRemove} variant="ghost" size="sm">
            {questionIndex != 0 && <Trash2 className="w-4 h-4 text-red-500" />}
          </Button>
        )}
      </div>

      <RhfInput
        name={`sections.${sectionIndex}.questions.${questionIndex}.question`}
        label="Question Text"
        required
      />

      <RhfSelect
        label="Answer Type"
        name={`sections.${sectionIndex}.questions.${questionIndex}.type`}
        options={[
          { label: "Multiple Choice", value: "multiple_choice" },
          { label: "Single Choice", value: "single_choice" },
          //   { label: "Single Input", value: "single_input" },
          { label: "Text Input", value: "textarea" },
          //   { label: "Multi Input", value: "multi_input" },
          //   { label: "Single Select", value: "single_select" },
          //   { label: "Multi Select", value: "multi_select" },
        ]}
        required
      />

      {showOptions && (
        <RhfMultiInput
          label="Options"
          name={`sections.${sectionIndex}.questions.${questionIndex}.options`}
        />
      )}
    </div>
  );
};
